import React, { useState, useEffect } from 'react';
import EnvVarPopupDialog, { EnvVar } from '../Components/EnvVarPopupDialog';
import { DeploymentConfig } from '../Model/Deployment';
import { useRepoViewModel } from '../ViewModel/RepoViewModel';
import { useConnectionStatus } from '../Components/ConnectionStatusProvider';

// ── Decode is_team_member from the JWT already in localStorage ───────────────
// No verification needed client-side — we just need the payload for UI gating.
function getTokenPayload(): Record<string, any> | null {
  try {
    const raw = localStorage.getItem('authToken') || '';
    const token = raw.startsWith('Bearer ') ? raw.slice(7) : raw;
    const payload = token.split('.')[1];
    if (!payload) return null;
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

const GITHUB_APP_SLUG = process.env.REACT_APP_GITHUB_APP_SLUG || '';
// Deep-link to the installation's repository-access screen (add/remove repos
// without uninstalling the app). Falls back to the general new-installation
// page if the slug env var isn't set.
const GITHUB_REPOS_URL = GITHUB_APP_SLUG
  ? `https://github.com/apps/${GITHUB_APP_SLUG}/installations/new`
  : 'https://github.com/settings/apps';

// ── Shared dark input styles ──────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: '#17171e',
  border: '1px solid #26262f',
  borderRadius: '6px',
  padding: '8px 11px',
  color: '#eeeef5',
  fontSize: '13px',
  fontFamily: "'Inter',sans-serif",
  outline: 'none',
  boxSizing: 'border-box' as const,
  transition: 'border-color .15s, box-shadow .15s',
};

const labelStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: 500,
  color: '#8e8ea8',
  display: 'block',
  marginBottom: '5px',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  cursor: 'pointer',
  appearance: 'none' as const,
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' fill='none' stroke='%2352525f' stroke-width='1.5'%3E%3Cpath d='M1 1l4 4 4-4'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 10px center',
  paddingRight: '30px',
};

// ── Component ─────────────────────────────────────────────────────────────────

interface StepOneProps {
  config: DeploymentConfig;
  onNext: () => void;
  repositoryOptions: { label: string; value: string }[];
  analyzeOptions: { label: string; value: string }[];
  deploymentEnv: { label: string; value: string }[];
  updateConfig: (updates: Partial<DeploymentConfig>) => void;
}

export const StepOne: React.FC<StepOneProps> = ({
  config,
  onNext,
  repositoryOptions,
  analyzeOptions,
  deploymentEnv,
  updateConfig,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const { showAWSModal, setShowAWSModal, checkConnectionStatus } = useConnectionStatus();
  const { branches, fetchBranches } = useRepoViewModel();

  const payload = getTokenPayload();
  const isTeamMember: boolean = payload?.is_team_member === true;

  useEffect(() => {
    if (config.repo_name) fetchBranches(config.repo_name);
  }, [config.repo_name]);

  const validateStep = () => {
    if (config.repo_name !== '' && config.branch?.trim() !== '' && config.app_name.trim() !== '') {
      onNext();
    }
  };

  const branchOptions = branches.map(branch => ({ label: branch, value: branch.toLowerCase() }));

  const envVarsArray = Object.entries(config.env_vars || {}).map(([key, value]) => ({ key, value }));
  const envVarCount = Object.keys(config.env_vars || {}).length;

  const addEnvVarFromDialog = (envVars: EnvVar[]) => {
    const envVariables: Record<string, string> = {};
    envVars.forEach(({ key, value }) => { if (key.trim() !== '') envVariables[key] = value; });
    updateConfig({ env_vars: envVariables });
    setIsDialogOpen(false);
  };

  const focusStyle = (field: string): React.CSSProperties =>
    focusedField === field
      ? { borderColor: '#6366f1', boxShadow: '0 0 0 3px rgba(99,102,241,0.14)' }
      : {};

  const fields = [
    {
      id: 'branch',
      label: 'Branch *',
      type: 'select' as const,
      value: config.branch || '',
      options: branchOptions,
      placeholder: !config.repo_name ? 'Select a repository first' : 'Select Branch',
      disabled: !config.repo_name,
      onChange: (e: React.ChangeEvent<HTMLSelectElement>) =>
        updateConfig({ branch: e.target.value }),
    },
    {
      id: 'deploymentEnv',
      label: 'Deployment Environment',
      type: 'select' as const,
      value: config.deployment_env,
      options: deploymentEnv,
      placeholder: 'Select deployment environment',
      disabled: false,
      onChange: (e: React.ChangeEvent<HTMLSelectElement>) =>
        updateConfig({ deployment_env: e.target.value }),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ marginBottom: '4px' }}>
        <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '15px', fontWeight: 700, color: '#eeeef5', marginBottom: '3px' }}>
          Application Details
        </div>
        <div style={{ fontSize: '12px', color: '#52525f' }}>Configure your repository and environment settings</div>
      </div>

      {/* Repository — handled separately so we can append the access hint */}
      <div>
        <label style={labelStyle}>Repository *</label>
        <select
          value={config.repo_name}
          onChange={e => updateConfig({ repo_name: e.target.value, branch: '' })}
          onFocus={() => setFocusedField('repo')}
          onBlur={() => setFocusedField(null)}
          style={{
            ...selectStyle,
            ...focusStyle('repo'),
            color: config.repo_name ? '#eeeef5' : '#52525f',
          }}
        >
          <option value="" disabled>Choose Repository</option>
          {repositoryOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        {/* Access hint — shown below the dropdown, different per role */}
        {isTeamMember ? (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: '7px',
            marginTop: '7px', padding: '8px 10px',
            background: '#17171e', border: '1px solid #26262f', borderRadius: '6px',
          }}>
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="#8e8ea8" strokeWidth="1.5" style={{ flexShrink: 0, marginTop: '1px' }}>
              <circle cx="6.5" cy="6.5" r="5.5"/><path d="M6.5 4v3M6.5 9v.5"/>
            </svg>
            <span style={{ fontSize: '11.5px', color: '#8e8ea8', lineHeight: 1.5 }}>
              Don't see a repo you need? Ask your organization owner to grant access to it via the GitHub App installation.
            </span>
          </div>
        ) : (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginTop: '7px', padding: '8px 10px',
            background: '#17171e', border: '1px solid #26262f', borderRadius: '6px',
          }}>
            <span style={{ fontSize: '11.5px', color: '#8e8ea8', lineHeight: 1.5 }}>
              Missing a repository? Add it to your GitHub App installation.
            </span>
            <a
              href={GITHUB_REPOS_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                fontSize: '11.5px', fontWeight: 600, color: '#818cf8',
                textDecoration: 'none', whiteSpace: 'nowrap' as const, flexShrink: 0, marginLeft: '10px',
              }}
            >
              Manage on GitHub
              <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M2 9L9 2M9 2H4.5M9 2v4.5"/>
              </svg>
            </a>
          </div>
        )}
      </div>

      {/* Remaining select fields */}
      {fields.map(field => (
        <div key={field.id}>
          <label style={labelStyle}>{field.label}</label>
          <select
            value={field.value}
            disabled={field.disabled}
            onChange={field.onChange}
            onFocus={() => setFocusedField(field.id)}
            onBlur={() => setFocusedField(null)}
            style={{
              ...selectStyle,
              ...focusStyle(field.id),
              opacity: field.disabled ? 0.4 : 1,
              cursor: field.disabled ? 'not-allowed' : 'pointer',
              color: field.value ? '#eeeef5' : '#52525f',
            }}
          >
            <option value="" disabled>{field.placeholder}</option>
            {field.options.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      ))}

      {/* App name */}
      <div>
        <label style={labelStyle}>Application Name *</label>
        <input
          type="text"
          value={config.app_name}
          onChange={e => updateConfig({ app_name: e.target.value })}
          onFocus={() => setFocusedField('appName')}
          onBlur={() => setFocusedField(null)}
          placeholder="e.g. my-backend-api"
          style={{ ...inputStyle, ...focusStyle('appName') }}
        />
      </div>

      {/* Env vars */}
      <div>
        <label style={labelStyle}>Environment Variables</label>
        <div
          onClick={() => setIsDialogOpen(true)}
          style={{
            ...inputStyle,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            color: envVarCount > 0 ? '#eeeef5' : '#52525f',
          }}
        >
          <span>{envVarCount > 0 ? `${envVarCount} variable${envVarCount > 1 ? 's' : ''} configured` : 'Click to add environment variables'}</span>
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="#52525f" strokeWidth="1.5">
            <path d="M6.5 2v9M2 6.5h9"/>
          </svg>
        </div>
        {envVarCount > 0 && (
          <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap' as const, gap: '5px' }}>
            {Object.keys(config.env_vars).slice(0, 4).map(key => (
              <span key={key} style={{ fontSize: '10.5px', fontFamily: "'IBM Plex Mono',monospace", background: '#17171e', border: '1px solid #26262f', borderRadius: '4px', padding: '2px 7px', color: '#8e8ea8' }}>
                {key}
              </span>
            ))}
            {Object.keys(config.env_vars).length > 4 && (
              <span style={{ fontSize: '10.5px', color: '#52525f' }}>+{Object.keys(config.env_vars).length - 4} more</span>
            )}
          </div>
        )}
      </div>

      {/* Next button */}
      <div style={{ paddingTop: '8px', borderTop: '1px solid #26262f', display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={validateStep}
          disabled={!config.repo_name || !config.branch || !config.app_name}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            padding: '8px 20px', borderRadius: '6px', fontSize: '13px',
            fontWeight: 500, cursor: !config.repo_name || !config.branch || !config.app_name ? 'not-allowed' : 'pointer',
            border: 'none', fontFamily: "'Inter',sans-serif",
            background: !config.repo_name || !config.branch || !config.app_name ? '#3d3f6b' : '#6366f1',
            color: '#fff',
            opacity: !config.repo_name || !config.branch || !config.app_name ? 0.5 : 1,
          }}
        >
          Continue to Configure
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M2.5 6.5h8M7 3l3.5 3.5L7 10"/>
          </svg>
        </button>
      </div>

      <EnvVarPopupDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onConfirm={addEnvVarFromDialog}
        initialEnvVars={envVarsArray}
      />
    </div>
  );
};